import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import BarcodeDisplay from "./BarcodeDisplay";
import "./Barcode.scss";

const PRINT_DOCUMENT_STYLES = `
	* { box-sizing: border-box; }
	html, body {
		margin: 0;
		padding: 0;
		height: auto;
		background: #fff;
		color: #000;
	}
	@page {
		size: auto;
		margin: 0.25in;
	}
	body {
		display: flex;
		justify-content: center;
		align-items: flex-start;
	}
	.barcode-print-root {
		width: 100%;
	}
	.barcode-print-area {
		display: flex;
		justify-content: center;
	}
	.barcode-label-sticker {
		border: 1px solid #000;
		padding: 10px 12px;
		width: 2.25in;
		min-height: 1.25in;
		text-align: center;
		background: #fff;
		page-break-inside: avoid;
		page-break-after: avoid;
	}
	.barcode-label-name {
		font-size: 11px;
		font-weight: 600;
		line-height: 1.2;
		margin-bottom: 4px;
		word-break: break-word;
	}
	.barcode-display svg {
		max-width: 100%;
		height: auto;
	}
`;

const buildPrintDocumentHtml = (labelHtml) => `<!DOCTYPE html>
<html lang="en">
<head>
	<meta charset="utf-8" />
	<title>Barcode Label</title>
	<style>${PRINT_DOCUMENT_STYLES}</style>
</head>
<body>
	<div class="barcode-print-root">
		<div class="barcode-print-area">
			${labelHtml}
		</div>
	</div>
</body>
</html>`;

const runPrintOnWindow = (printWindow, labelHtml) => {
	const doc = printWindow.document;
	doc.open();
	doc.write(buildPrintDocumentHtml(labelHtml));
	doc.close();

	const triggerPrint = () => {
		printWindow.focus();
		printWindow.print();
	};

	const cleanup = () => {
		try {
			if (printWindow.frameElement?.parentNode) {
				printWindow.frameElement.parentNode.removeChild(printWindow.frameElement);
			} else if (!printWindow.closed) {
				printWindow.close();
			}
		} catch {
			/* ignore */
		}
	};

	printWindow.onafterprint = cleanup;

	if (doc.readyState === "complete") {
		triggerPrint();
	} else {
		printWindow.addEventListener("load", triggerPrint, { once: true });
	}
};

/**
 * Print via hidden iframe — no pop-up permission required.
 */
const printBarcodeViaIframe = (labelHtml) => {
	const iframe = document.createElement("iframe");
	iframe.setAttribute("title", "Barcode label print");
	iframe.style.cssText =
		"position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
	document.body.appendChild(iframe);

	const printWindow = iframe.contentWindow;
	if (!printWindow) {
		document.body.removeChild(iframe);
		return false;
	}

	try {
		runPrintOnWindow(printWindow, labelHtml);
		// Fallback cleanup if afterprint is not supported
		setTimeout(() => {
			if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
		}, 60000);
		return true;
	} catch {
		if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
		return false;
	}
};

/**
 * Print via new tab/window. Do not pass noopener — browsers return null but still open the tab.
 */
const printBarcodeViaWindow = (labelHtml) => {
	const printWindow = window.open("", "_blank", "width=420,height=320");
	if (!printWindow) return false;

	try {
		if (printWindow.opener) printWindow.opener = null;
		runPrintOnWindow(printWindow, labelHtml);
		return true;
	} catch {
		try {
			printWindow.close();
		} catch {
			/* ignore */
		}
		return false;
	}
};

const printBarcodeLabel = (labelHtml) =>
	printBarcodeViaIframe(labelHtml) || printBarcodeViaWindow(labelHtml);

/**
 * Printable barcode label — one label per print job via a dedicated print window.
 */
const BarcodePrint = ({
	items = [],
	onClose,
	showActions = true,
}) => {
	const { t } = useTranslation();
	const labelRef = useRef(null);
	const [printIndex, setPrintIndex] = useState(0);

	const validItems = items.filter((item) => String(item?.barcode || "").trim());

	useEffect(() => {
		setPrintIndex(0);
	}, [items]);

	const safeIndex = Math.min(printIndex, Math.max(0, validItems.length - 1));
	const currentItem = validItems[safeIndex];

	const handlePrint = useCallback(() => {
		if (!currentItem || !labelRef.current) return;
		const labelHtml = labelRef.current.outerHTML;
		if (!printBarcodeLabel(labelHtml)) {
			window.alert(t("Unable to open print preview. Please try again."));
		}
	}, [currentItem, t]);

	if (!validItems.length) {
		return (
			<p className="text-muted mb-0">{t("No barcode available to print")}</p>
		);
	}

	const hasMultiple = validItems.length > 1;

	return (
		<div className="barcode-print-root">
			{hasMultiple && (
				<div className="d-flex justify-content-center align-items-center gap-2 mb-2 no-print">
					<Button
						variant="outline-secondary"
						size="sm"
						disabled={safeIndex <= 0}
						onClick={() => setPrintIndex((i) => Math.max(0, i - 1))}
					>
						{t("Previous")}
					</Button>
					<span className="small text-muted">
						{safeIndex + 1} / {validItems.length}
					</span>
					<Button
						variant="outline-secondary"
						size="sm"
						disabled={safeIndex >= validItems.length - 1}
						onClick={() =>
							setPrintIndex((i) => Math.min(validItems.length - 1, i + 1))
						}
					>
						{t("Next")}
					</Button>
				</div>
			)}
			<div className="barcode-print-area" id="barcode-print-area">
				<div
					ref={labelRef}
					id="barcode-print-label"
					className="barcode-label-sticker"
				>
					<div className="barcode-label-name">{currentItem.name}</div>
					<BarcodeDisplay
						value={currentItem.barcode}
						displayValue
						width={1.4}
						height={44}
						showPlaceholder={false}
					/>
				</div>
			</div>
			{showActions && (
				<div className="barcode-print-actions d-flex justify-content-center gap-2 mt-3 no-print">
					<Button variant="primary" size="sm" onClick={handlePrint}>
						{t("Print")}
					</Button>
					{onClose && (
						<Button variant="secondary" size="sm" onClick={onClose}>
							{t("Close")}
						</Button>
					)}
				</div>
			)}
		</div>
	);
};

export default BarcodePrint;
